// LineasPanelXL - Sprint 44 Fase 3 Vista 10.
// Lineas de importacion: agregar, listar, eliminar + boton distribuir.
import { useCallback, useEffect, useState } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import apiClient from '../../api/apiClient';
import XLInput from '../../components/xl/XLInput';
import XLMoneyInput from '../../components/xl/XLMoneyInput';
import XLSelect from '../../components/xl/XLSelect';
import XLSkeleton from '../../components/xl/XLSkeleton';
import { MONEDA_OPTIONS, fmtNum } from './utils';

const CSS_ID = 'xl-imp-lineas-css';
const CSS = `
.xl-impl { display: flex; flex-direction: column; gap: 16px; font-family: var(--xl-font-sans) }
.xl-impl__addbar {
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-md);
  padding: 14px 16px;
}
.xl-impl__addbar-title {
  font-size: 11px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--xl-color-text-muted); margin-bottom: 10px;
}
.xl-impl__addbar-grid {
  display: grid; grid-template-columns: 5fr 2fr 2fr 1fr 2fr; gap: 10px; align-items: end;
}
@media (max-width: 1000px) { .xl-impl__addbar-grid { grid-template-columns: 1fr 1fr; } }
.xl-impl__btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
  background: var(--xl-color-primary); color: var(--xl-color-text-inverse);
}
.xl-impl__btn:hover { background: var(--xl-color-primary-hover) }
.xl-impl__btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-impl__btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-impl__btn--indigo { background: #6366F1 }
.xl-impl__btn--indigo:hover { filter: brightness(1.1) }
[data-xl-theme="dark"] .xl-impl__btn--indigo { background: #818CF8 }
.xl-impl__distribuir-row { display: flex; justify-content: flex-end }

.xl-impl__table-wrap {
  overflow-x: auto;
  border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg);
  background: var(--xl-color-surface);
  box-shadow: var(--xl-shadow-sm);
}
.xl-impl__table { width: 100%; min-width: 880px; border-collapse: collapse; font-size: 13px }
.xl-impl__table thead th {
  background: var(--xl-color-bg); color: var(--xl-color-text-secondary);
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.05em;
  text-align: left; padding: 10px 12px;
  border-bottom: 1px solid var(--xl-color-border);
  white-space: nowrap;
}
.xl-impl__table td {
  padding: 8px 12px; border-bottom: 1px solid var(--xl-color-border);
  color: var(--xl-color-text);
}
.xl-impl__table tr:last-child td { border-bottom: 0 }
.xl-impl__del {
  background: transparent; border: none; cursor: pointer;
  color: var(--xl-color-danger); font-size: 12px;
  padding: 4px 8px; border-radius: var(--xl-radius-sm);
}
.xl-impl__del:hover { background: rgba(220,38,38,0.08) }
.xl-impl__empty { padding: 28px 16px; text-align: center; color: var(--xl-color-text-muted); font-size: 13px }
.xl-impl__placeholder { padding: 16px; color: var(--xl-color-text-muted); font-size: 13px }
.xl-mono { font-family: var(--xl-font-mono); font-size: 12px }
.xl-mono--good { color: var(--xl-color-success); font-weight: 600 }
.xl-td-right { text-align: right }
`;

const NUEVA_INICIAL = {
  producto_descripcion: '',
  cantidad: '',
  costo_proveedor_unitario: '',
  costo_proveedor_moneda: 'USD',
};

export default function LineasPanelXL({ importacionId, isNew, onDistribuir }) {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nueva, setNueva] = useState(NUEVA_INICIAL);

  const load = useCallback(async () => {
    if (!importacionId) return;
    setLoading(true);
    try {
      const r = await apiClient.get('/importaciones/' + importacionId + '/lineas');
      setItems((r && r.data && r.data.items) || []);
    } finally { setLoading(false); }
  }, [importacionId]);

  useEffect(() => { load(); }, [load]);

  const onAdd = async () => {
    if (!importacionId) return;
    if (!nueva.cantidad || !nueva.costo_proveedor_unitario) {
      alert('Cantidad y costo unitario son requeridos');
      return;
    }
    try {
      await apiClient.post('/importaciones/' + importacionId + '/lineas', {
        producto_descripcion: nueva.producto_descripcion,
        cantidad: Number(nueva.cantidad),
        costo_proveedor_unitario: Number(nueva.costo_proveedor_unitario),
        costo_proveedor_moneda: nueva.costo_proveedor_moneda,
      });
      setNueva(NUEVA_INICIAL);
      await load();
    } catch (e) { alert((e && e.message) || 'Error al crear linea'); }
  };

  const onDelete = async (l) => {
    if (!window.confirm('Eliminar linea?')) return;
    try {
      await apiClient.delete('/importaciones/lineas/' + l.id);
      await load();
    } catch (e) { alert((e && e.message) || 'Error'); }
  };

  if (isNew && !importacionId) {
    return (
      <div className="xl-impl__placeholder">
        Guarda la importacion primero para agregar lineas.
      </div>
    );
  }

  return (
    <div className="xl-impl">
      <div className="xl-impl__addbar">
        <div className="xl-impl__addbar-title">Agregar linea</div>
        <div className="xl-impl__addbar-grid">
          <XLInput label="Producto / descripcion"
            value={nueva.producto_descripcion}
            onChange={(e) => setNueva({ ...nueva, producto_descripcion: e.target.value })} />
          <XLInput label="Cantidad" type="number" min="0" step="0.01"
            value={nueva.cantidad}
            onChange={(e) => setNueva({ ...nueva, cantidad: e.target.value })} />
          <XLMoneyInput label="Costo unit."
            value={nueva.costo_proveedor_unitario || 0}
            onChange={(n) => setNueva({ ...nueva, costo_proveedor_unitario: n == null ? '' : n })} />
          <XLSelect label="Moneda" value={nueva.costo_proveedor_moneda}
            onChange={(e) => setNueva({ ...nueva, costo_proveedor_moneda: e.target.value })}
            options={MONEDA_OPTIONS} />
          <button type="button" className="xl-impl__btn" onClick={onAdd}>
            Agregar
          </button>
        </div>
      </div>

      <div className="xl-impl__distribuir-row">
        <button type="button" className="xl-impl__btn xl-impl__btn--indigo"
          onClick={onDistribuir}
          disabled={isNew || items.length === 0}>
          Distribuir costos
        </button>
      </div>

      <div className="xl-impl__table-wrap">
        <table className="xl-impl__table">
          <thead>
            <tr>
              <th>#</th>
              <th>Producto</th>
              <th className="xl-td-right">Cant.</th>
              <th className="xl-td-right">CU prov.</th>
              <th className="xl-td-right">CU prov. USD</th>
              <th className="xl-td-right">CU final USD</th>
              <th className="xl-td-right">CU final COP</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 2 }).map((_, i) => (
              <tr key={'sk-' + i}>
                <td colSpan={8}><XLSkeleton variant="line" width="80%" height={18} /></td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={8} className="xl-impl__empty">Sin lineas todavia.</td></tr>
            )}
            {!loading && items.map((l, i) => (
              <tr key={l.id}>
                <td className="xl-mono">{i + 1}</td>
                <td>{l.producto_descripcion || ('#' + (l.producto_id || ''))}</td>
                <td className="xl-td-right xl-mono">{fmtNum(l.cantidad)}</td>
                <td className="xl-td-right xl-mono">
                  {fmtNum(l.costo_proveedor_unitario)} {l.costo_proveedor_moneda}
                </td>
                <td className="xl-td-right xl-mono">{fmtNum(l.costo_proveedor_unitario_usd)}</td>
                <td className="xl-td-right xl-mono">{fmtNum(l.costo_unitario_final_usd)}</td>
                <td className="xl-td-right xl-mono--good">{fmtNum(l.costo_unitario_final_cop)}</td>
                <td className="xl-td-right">
                  <button type="button" className="xl-impl__del" onClick={() => onDelete(l)}>
                    Eliminar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
